/**
 * VM family slugs (#2043 · F1).
 *
 * SOURCE OF TRUTH IS THE DATABASE: `bat.vm_families` decides which families
 * exist and which are enabled. This list exists ONLY so the UI can render
 * labels / pickers without a round-trip and so tests have a stable fixture.
 *
 * Runtime code MUST keep `vmFamily` typed as `string` and validate against the
 * table (see the admin `BlockchainNetworksService.assertVmFamilyIsEnabled`) —
 * never narrow it to `KnownVmFamilySlug`, or a family added by INSERT would
 * be rejected by the type system until the next deploy.
 */
import { VALIDATION_PATTERNS } from '../utils/validators.js'

export const KNOWN_VM_FAMILY_SLUGS = ['evm', 'svm', 'utxo', 'unmapped'] as const

export type KnownVmFamilySlug = (typeof KNOWN_VM_FAMILY_SLUGS)[number]

/**
 * The family whose rows carry the EVM-shaped body columns (`value_wei`,
 * `input_data`, the `gas_*` trio) and that the EVM classifier can read.
 *
 * It lives HERE, next to `KNOWN_VM_FAMILY_SLUGS`, and not in the staging data
 * layer: writers of `vm_family` (the acquisition stage), readers of it (the
 * classification stage) and the write-path guards all need the same literal,
 * and a job stage must not import a repository guard file to get a string
 * (#2045 · F3 review · item 10).
 */
export const EVM_VM_FAMILY_SLUG: KnownVmFamilySlug = 'evm'

/**
 * The family of the Solana Virtual Machine.
 *
 * An SVM staging row is the MIRROR IMAGE of an EVM one: `value_wei`,
 * `input_data` and the whole `gas_*` trio are NULL by design (Solana meters
 * compute units, not gas), the flat `meta.fee` goes to `fee_native_amount` in
 * LAMPORTS, and the economic facts live in `raw_payload` — `meta.pre/postBalances`
 * for native lamports and `meta.pre/postTokenBalances` for SPL.
 *
 * It lives HERE, next to its EVM sibling, because BOTH SIDES of the pipeline
 * need the same literal and neither may import the other to get it: the SVM
 * acquisition strategy WRITES it (#2045 · F3), and the classification stage,
 * the SVM classifier and the write-path guards READ it (#2047 · F5).
 */
export const SVM_VM_FAMILY_SLUG: KnownVmFamilySlug = 'svm'

/**
 * Non-EVM chains have no EIP-155 chain id, so BAT assigns SYNTHETIC ids in
 * the `>= 990000` band (negative ids are the internal placeholder band). The
 * DB CHECK `blockchain_networks_synthetic_id_band` (migration 0344 /
 * `blockchain-networks.schema.ts`) is the enforcing copy; the admin service,
 * the seed validator and the admin form read THIS constant so all four agree.
 */
export const NON_EVM_SYNTHETIC_CHAIN_ID_MIN = 990000

/**
 * Fallback address regex for EVM networks whose `address_pattern` is NULL.
 * ONE source: the `RegExp` in `VALIDATION_PATTERNS.ETHEREUM_ADDRESS` — its
 * `.source` is the string mirrored by `bat.vm_families.default_address_pattern`
 * for slug 'evm'.
 */
export const DEFAULT_EVM_ADDRESS_PATTERN: string = VALIDATION_PATTERNS.ETHEREUM_ADDRESS.source

/** EVM transaction hash — mirrors `bat.vm_families.default_txid_pattern` for 'evm'. */
export const DEFAULT_EVM_TXID_PATTERN = '^0x[a-fA-F0-9]{64}$'

/**
 * SVM (Solana) defaults — base58 alphabet (no 0, O, I, l). Mirror the 'svm'
 * row of `bat.vm_families` (migration 0344). ONE definition: backend seeds,
 * test fixtures and the frontend family-default form all reference these.
 */
export const DEFAULT_SVM_ADDRESS_PATTERN = '^[1-9A-HJ-NP-Za-km-z]{32,44}$'
export const DEFAULT_SVM_TXID_PATTERN = '^[1-9A-HJ-NP-Za-km-z]{86,88}$'

/**
 * UTXO (Bitcoin) defaults — bech32 (`bc1…`) OR legacy base58 (`1…`/`3…`)
 * addresses; 64-hex txids. Mirror the 'utxo' row of `bat.vm_families`.
 */
export const DEFAULT_UTXO_ADDRESS_PATTERN = '^(bc1[a-z0-9]{39,59}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$'
export const DEFAULT_UTXO_TXID_PATTERN = '^[0-9a-fA-F]{64}$'

/**
 * Plausible JSON-RPC method name for a liveness probe (`eth_chainId`,
 * `getHealth`, `getblockchaininfo`, `get_ledger_info`): a letter, then up to
 * 63 letters / digits / underscores. ONE definition (fix cycle 7 · item 10)
 * for the admin zod `healthCheckMethodSchema`, the frontend form validation and
 * both seed validators, so a method the form accepts is never one the API or
 * the seed refuses. Spaces, hyphens and a leading digit are all refused.
 */
export const HEALTH_CHECK_METHOD_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/

/**
 * Probe defaults of one `bat.vm_families` row — the DB columns
 * `default_health_check_*` as the admin API DTO (`GET /vm-families`) and the
 * seed carry them. A network whose own `health_check_*` columns are NULL
 * INHERITS these at read time (`blockchain-network-chain-config.ts`), exactly
 * like `address_pattern` / `txid_pattern`.
 */
export interface VmFamilyProbeDefaults {
  /** `null` = the family has no default probe: a network must state its own method. */
  defaultHealthCheckMethod: string | null
  defaultHealthCheckParams: unknown[]
  defaultHealthCheckExpectation: Record<string, unknown> | null
}

/**
 * Per-family RPC liveness probe defaults — SEED DATA ONLY (fix cycle 6 · item 8).
 * The runtime source of truth is `bat.vm_families.default_health_check_*`
 * (migration 0344): the backend reads the family row hydrated by the
 * repository / registry join, and the admin form reads
 * `GET /admin/system/blockchain-networks/vm-families`. This map exists so
 * `vm-families.seed.ts` (and its offline validator) ship the same values the
 * migration INSERTs; nothing at runtime may branch on it.
 */
export const DEFAULT_EVM_HEALTH_CHECK_METHOD = 'eth_chainId'
export const DEFAULT_SVM_HEALTH_CHECK_METHOD = 'getHealth'
export const DEFAULT_UTXO_HEALTH_CHECK_METHOD = 'getblockchaininfo'
/** Solana's `getHealth` answers the literal string "ok". */
export const DEFAULT_SVM_HEALTH_CHECK_EXPECTATION: Readonly<Record<string, unknown>> = {
  equalsString: 'ok',
}

export interface VmFamilyHealthCheckDefaults {
  method: string
  /** `null` = any successful reply passes. */
  expectation: Readonly<Record<string, unknown>> | null
}

export const VM_FAMILY_HEALTH_CHECK_DEFAULTS: Readonly<
  Record<string, VmFamilyHealthCheckDefaults>
> = {
  evm: { method: DEFAULT_EVM_HEALTH_CHECK_METHOD, expectation: null },
  svm: {
    method: DEFAULT_SVM_HEALTH_CHECK_METHOD,
    expectation: DEFAULT_SVM_HEALTH_CHECK_EXPECTATION,
  },
  utxo: { method: DEFAULT_UTXO_HEALTH_CHECK_METHOD, expectation: null },
}
